import { z } from "zod";
import { userRoleSchema } from "./auth.js";
import { isoDateSchema } from "./common.js";
import { isMatchableAddress } from "./locationMatch.js";

// =============================================================================
// Admin schemas
//
// Wire shapes for /api/admin/*. Reused by the admin React pages so the
// types line up with what the BFF actually returns.
// =============================================================================

// ----- Users list ----------------------------------------------------------

export const adminUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  /** Optional callback phone (digits-only). Empty when the user
   *  hasn't supplied one. Surfaced in the admin Edit User dialog so
   *  a Super Admin can fill it in on someone's behalf. */
  phone: z.string(),
  role: userRoleSchema,
  department: z.string(),
  isActive: z.boolean(),
  emailVerifiedAt: z.string().nullable(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

/** Filterable fields the users list accepts. Cursor pagination is on
 *  (createdAt, id) descending — newer rows first — so "next page" means
 *  "rows older than the cursor". */
export const adminUsersQuerySchema = z.object({
  search: z.string().max(200).optional(),
  role: userRoleSchema.optional(),
  /** "active" / "disabled" / "never_logged_in" — matches the filter chips. */
  status: z.enum(["active", "disabled", "never_logged_in"]).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  beforeCreatedAt: z.string().optional(),
  beforeId: z.string().uuid().optional(),
});
export type AdminUsersQuery = z.infer<typeof adminUsersQuerySchema>;

/** Aggregated counts that drive the StatCard row at the top of the page.
 *  Always computed against the *unfiltered* users table so flipping a
 *  filter chip can't change the totals next to it (otherwise picking
 *  "Active" would make the "Total" tile change too — confusing). */
export const adminUsersCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  admins: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  neverLoggedIn: z.number().int().nonnegative(),
});
export type AdminUsersCounts = z.infer<typeof adminUsersCountsSchema>;

export const adminUsersListResponseSchema = z.object({
  users: z.array(adminUserSchema),
  hasMore: z.boolean(),
  nextCursor: z
    .object({
      createdAt: z.string(),
      id: z.string().uuid(),
    })
    .nullable(),
  /** Total rows that match the active filter (capped at 1000 for sanity). */
  matchingTotal: z.number().int().nonnegative(),
  counts: adminUsersCountsSchema,
});
export type AdminUsersListResponse = z.infer<typeof adminUsersListResponseSchema>;

// ----- Invitations list ----------------------------------------------------

export const adminInvitationSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  role: userRoleSchema,
  department: z.string(),
  expiresAt: z.string(),
  /** Null for system-issued invitations (e.g. the bootstrap super-admin
   *  flow on the very first deploy). */
  invitedByUserId: z.string().uuid().nullable(),
  invitedByName: z.string(),
  createdAt: z.string(),
});
export type AdminInvitation = z.infer<typeof adminInvitationSchema>;

export const adminInvitationsQuerySchema = z.object({
  search: z.string().max(200).optional(),
  role: userRoleSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  beforeCreatedAt: z.string().optional(),
  beforeId: z.string().uuid().optional(),
});
export type AdminInvitationsQuery = z.infer<typeof adminInvitationsQuerySchema>;

export const adminInvitationsListResponseSchema = z.object({
  invitations: z.array(adminInvitationSchema),
  hasMore: z.boolean(),
  nextCursor: z
    .object({
      createdAt: z.string(),
      id: z.string().uuid(),
    })
    .nullable(),
  /** Total active invitations matching the filter. */
  matchingTotal: z.number().int().nonnegative(),
  /** Absolute count of pending invitations (drives the "Pending invites"
   *  StatCard on the users page). */
  pendingTotal: z.number().int().nonnegative(),
});
export type AdminInvitationsListResponse = z.infer<typeof adminInvitationsListResponseSchema>;

// ----- Invite a user -------------------------------------------------------

export const adminInviteUserRequestSchema = z.object({
  email: z
    .string()
    .min(3)
    .max(200)
    .transform((v) => v.trim().toLowerCase())
    .refine(
      (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u.test(v),
      "Enter a valid email address.",
    ),
  role: userRoleSchema,
  department: z.string().max(100).default("").transform((v) => v.trim()),
});
export type AdminInviteUserRequest = z.infer<typeof adminInviteUserRequestSchema>;

export const adminInviteUserResponseSchema = z.object({
  invitation: adminInvitationSchema,
});
export type AdminInviteUserResponse = z.infer<typeof adminInviteUserResponseSchema>;

// =============================================================================
// Bulk resend pending invitations.
//
// Refreshes the token + extends `expires_at` on every still-pending
// invitation that matches the filters, then re-mails each one.
// Designed for the "we sent 167 invites last week and they're all
// about to expire" scenario — admins shouldn't have to click Resend
// 167 times.
//
// RBAC mirrors the single-invite endpoint:
//   * `admin` can only bulk-resend invitations whose role is `user`
//     (the server filters down regardless of what the client asked
//     for, so a forged request can't escalate).
//   * `super_admin` can bulk-resend everything.
//
// Filters:
//   * `expiringWithinDays` — restrict to invites whose existing
//     `expires_at` is within the next N days. Useful for "rescue the
//     ones about to die" workflows without re-mailing freshly-sent
//     ones.
//   * `role` — narrows by role.
// =============================================================================

export const adminResendInvitationsBulkRequestSchema = z.object({
  /** Only resend invitations whose current `expires_at` is at most
   *  N days in the future. Omit to resend every pending invite that
   *  matches the role filter. Capped at 30 because past that point
   *  the invitations are already so fresh that re-mailing is just
   *  spam. */
  expiringWithinDays: z.number().int().min(0).max(30).optional(),
  /** Role narrow. Admins get force-narrowed to `user` server-side
   *  regardless of what they ask for. */
  role: userRoleSchema.optional(),
});
export type AdminResendInvitationsBulkRequest = z.infer<
  typeof adminResendInvitationsBulkRequestSchema
>;

export const adminResendInvitationsBulkResponseSchema = z.object({
  /** Invitations that matched the filter — counted before any
   *  sending happens so the UI can describe the scope of the
   *  operation. */
  totalCandidates: z.number().int().nonnegative(),
  /** Successful re-sends (email handed off to SendGrid OK). */
  resent: z.number().int().nonnegative(),
  /** Failed re-sends (SendGrid error, malformed address, etc.). */
  failed: z.number().int().nonnegative(),
  /** First few errors for surfacing in the result toast / dialog.
   *  Capped server-side so the response stays small even when the
   *  whole batch fails. */
  errors: z
    .array(
      z.object({
        email: z.string(),
        message: z.string(),
      }),
    )
    .max(20),
});
export type AdminResendInvitationsBulkResponse = z.infer<
  typeof adminResendInvitationsBulkResponseSchema
>;

// ----- Patch a user --------------------------------------------------------

export const adminUpdateUserRequestSchema = z
  .object({
    role: userRoleSchema.optional(),
    department: z.string().max(100).optional(),
    isActive: z.boolean().optional(),
    firstName: z.string().min(1).max(50).optional(),
    lastName: z.string().min(1).max(50).optional(),
    /** Optional. Server normalizes to digits-only so display formats
     *  consistently. Empty string explicitly clears the user's saved
     *  phone (different from "not provided" which leaves it alone). */
    phone: z.string().max(40).optional(),
    /** Email change is super-admin only on the API side. Lowercased on
     *  storage, must be unique across users. Changing email
     *  invalidates any outstanding magic-links / password-reset tokens
     *  the user had pending under the old address. */
    email: z
      .string()
      .min(3)
      .max(200)
      .transform((v) => v.trim().toLowerCase())
      .pipe(z.string().email())
      .optional(),
  })
  .refine(
    (v) => Object.keys(v).length > 0,
    "At least one field has to be supplied.",
  );
export type AdminUpdateUserRequest = z.infer<typeof adminUpdateUserRequestSchema>;

export const adminUpdateUserResponseSchema = z.object({
  user: adminUserSchema,
});
export type AdminUpdateUserResponse = z.infer<typeof adminUpdateUserResponseSchema>;

// ----- Revoke sessions / delete invitation --------------------------------

export const adminEmptyOkResponseSchema = z.object({ ok: z.literal(true) });
export type AdminEmptyOkResponse = z.infer<typeof adminEmptyOkResponseSchema>;

// ----- Audit log -----------------------------------------------------------

export const adminAuditEntrySchema = z.object({
  id: z.string(),
  occurredAt: z.string(),
  action: z.string(),
  outcome: z.string(),
  targetType: z.string(),
  targetId: z.string(),
  ipAddress: z.string(),
  userAgent: z.string(),
  userId: z.string().nullable(),
  userEmail: z.string().nullable(),
  userFirstName: z.string().nullable(),
  userLastName: z.string().nullable(),
  metadata: z.record(z.unknown()).default({}),
});
export type AdminAuditEntry = z.infer<typeof adminAuditEntrySchema>;

export const adminAuditQuerySchema = z.object({
  /** Filter by exact action prefix (e.g. "patient." catches everything
   *  starting with patient.). Empty = no filter. */
  action: z.string().max(64).optional(),
  /** Filter by user uuid; "anonymous" surfaces rows with userId null. */
  userId: z.string().max(100).optional(),
  outcome: z.string().max(16).optional(),
  /** ISO timestamp lower bound (inclusive). */
  since: z.string().optional(),
  /** ISO timestamp upper bound (inclusive). */
  until: z.string().optional(),
  /** When false (default), hide high-volume system / background
   *  actions (e.g. `triplink.sync` fires every 5 minutes around the
   *  clock). The actions are still selectable from the Action
   *  dropdown — picking one explicitly overrides this hide. */
  includeSystem: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => v === true || v === "true" || v === "1"),
  /** Page size; capped server-side. */
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** Pagination cursor — id of the last row from the previous page. */
  beforeId: z.string().optional(),
});
export type AdminAuditQuery = z.infer<typeof adminAuditQuerySchema>;

export const adminAuditListResponseSchema = z.object({
  entries: z.array(adminAuditEntrySchema),
  /** True when more rows are available past the last item. */
  hasMore: z.boolean(),
  /** Cursor to pass as `beforeId` for the next page. */
  nextCursor: z.string().nullable(),
  /** Distinct list of actions seen — handy for the filter dropdown. */
  actions: z.array(z.string()),
  /** Action names the server considers "system noise" and hides by
   *  default. The Audit page uses this to label the
   *  "Show system events" toggle accurately without hard-coding the
   *  action names client-side. */
  systemActions: z.array(z.string()).default([]),
});
export type AdminAuditListResponse = z.infer<typeof adminAuditListResponseSchema>;

// ----- Bookings explorer ---------------------------------------------------

export const adminBookingSchema = z.object({
  id: z.string().uuid(),
  triplinkFriendlyId: z.string().nullable(),
  triplinkGroupId: z.string().nullable(),
  triplinkLeg: z.string().nullable(),

  mrn: z.string(),
  dob: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  department: z.string(),
  costCenter: z.string(),

  orderType: z.string(),
  tripType: z.string(),
  transportType: z.string(),

  transportDate: z.string().nullable(),
  pickupAddress: z.string(),
  pickupCityZip: z.string(),
  pickupLocation: z.string(),
  pickupTimeEstimated: z.string(),
  appointmentTime: z.string(),
  appointmentBufferMinutes: z.number().nullable(),
  timeType: z.string(),
  pickupNotes: z.string(),

  destinationAddress: z.string(),
  destinationCityZip: z.string(),
  dropoffNotes: z.string(),

  returnTimeMode: z.string(),
  returnPickupTime: z.string(),
  returnToOriginalPickup: z.boolean(),
  returnCustomDestinationAddress: z.string(),
  returnCustomDestinationCityZip: z.string(),
  returnLegNotes: z.string(),

  additionalLegs: z.array(z.record(z.unknown())).default([]),

  contactPhone: z.string(),
  requestedBy: z.string(),
  callbackPhone: z.string(),
  requesterEmail: z.string(),

  source: z.string(),
  createdAt: z.string(),
  clientIp: z.string(),
  createdByUserId: z.string().uuid().nullable(),
  createdByEmail: z.string().nullable(),
  createdByFirstName: z.string().nullable(),
  createdByLastName: z.string().nullable(),
  /** Live status from triplink as of the last sync. Empty when the row
   *  hasn't been touched by the sync yet (e.g. a freshly-submitted V2
   *  booking the timer hasn't picked up). */
  triplinkStatus: z.string(),
  /** True when the row was originally inserted by the triplink sync
   *  (so the booking came in via V1 form or phone, not our V2 form).
   *  Drives the "TMS" vs "FORM" origin badge in the admin list. */
  triplinkSynced: z.boolean(),
  canceledAt: z.string().nullable(),
  /** Trip distance in miles (from TripLink's feed). Null when not yet
   *  provided. Only present in the response when the requester has the
   *  `viewMileageRate` permission — omitted (undefined) otherwise. */
  mileage: z.number().nullable().optional(),
  /** Transport fare in integer cents (base + per-mile-over-15, by
   *  service type and day/night pickup window). $0 for cancelled trips
   *  (IT fee only billing); null when not priceable. Like `mileage`,
   *  only present with the `viewMileageRate` permission. */
  fareCents: z.number().int().nullable().optional(),
  /** Base-fare component of the fare (for the breakdown UI). Null when
   *  the fare is null or the trip is cancelled. */
  fareBaseCents: z.number().int().nullable().optional(),
  /** Per-mile component of the fare (0 within the free 15 miles).
   *  Null in the same cases as `fareBaseCents`. */
  fareMileageCents: z.number().int().nullable().optional(),
  /** Flat IT fee in cents — charged on every trip, including
   *  cancellations. Null only for tombstoned (deleted-upstream) rows,
   *  which bill nothing. */
  itFeeCents: z.number().int().nullable().optional(),
  /** Grand total in cents: fare + fees. Null when the fare can't be
   *  computed yet (or the row is tombstoned). */
  totalCents: z.number().int().nullable().optional(),
  /** The fare the rate engine would compute WITHOUT any override —
   *  lets the UI strike it through next to a hand-set fare. Equals
   *  `fareCents` when no override is in effect; null when the trip
   *  isn't priceable. Present only with `viewMileageRate`. */
  computedFareCents: z.number().int().nullable().optional(),
  /** Hand-set per-leg fare in cents (Royal super-admin override).
   *  Null = no override. When set on a live leg, `fareCents` IS this
   *  number and the IT fee still applies on top. Present only with
   *  `viewMileageRate`, like the other money fields. */
  fareOverrideCents: z.number().int().nullable().optional(),
  /** Why the fare was overridden ("" when no override). */
  fareOverrideNote: z.string().optional(),
  /** When the override was set (ISO timestamp) — null when none. */
  fareOverrideAt: z.string().nullable().optional(),
  /** Display name (or email) of the admin who set the override —
   *  null when none, or when their account was since deleted. */
  fareOverrideBy: z.string().nullable().optional(),
  /** Who cancelled this leg through the portal (its trip.cancel audit
   *  row). Null when it isn't cancelled, or was cancelled outside the
   *  portal (in TripLink). `name` is null once their account is gone. */
  canceledBy: z
    .object({
      name: z.string().nullable(),
      email: z.string().nullable(),
      at: z.string(),
      reason: z.string().nullable(),
    })
    .nullable()
    .optional(),
  /** An admin's approval of this off-site trip, sent with the Off-site
   *  trips view only. `stale` when the trip's addresses have changed since
   *  it was approved: the approval then no longer counts, and the trip is
   *  back under review. */
  offsiteApproval: z
    .object({
      approvedAt: z.string(),
      approvedBy: z.string().nullable(),
      note: z.string(),
      stale: z.boolean(),
    })
    .nullable()
    .optional(),
});
export type AdminBooking = z.infer<typeof adminBookingSchema>;

/** PUT /api/admin/bookings/:id/fare-override — hand-set one leg's fare.
 *  Royal-team super-admins only (enforced server-side). The amount
 *  replaces the computed fare; the IT fee is still added automatically;
 *  a cancelled leg keeps billing the IT fee only regardless. */
export const adminFareOverrideSetRequestSchema = z.object({
  /** The override fare in integer cents. ≥ 0 — a $0.00 override (comp'd
   *  leg) is legal; negatives are not. */
  fareCents: z.number().int().min(0).max(10_000_000),
  /** Required human reason — shown in the bookings UI and audit log. */
  note: z.string().trim().min(1).max(500),
});
export type AdminFareOverrideSetRequest = z.infer<
  typeof adminFareOverrideSetRequestSchema
>;

export const adminBookingsQuerySchema = z.object({
  /** Free-text match against MRN, patient name, friendly ID, requester,
   *  department. Empty = no text filter. */
  search: z.string().max(200).optional(),
  /** Exact-MRN filter. Use this (not `search`) when you specifically
   *  want a single patient's bookings — `search` does an OR ILIKE
   *  across name/department/etc. and can mix patients on a substring
   *  collision. Used by the Patient History dialog. */
  mrn: z.string().max(200).optional(),
  /** ISO YYYY-MM-DD lower bound on transport_date (inclusive). */
  dateFrom: z.string().optional(),
  /** ISO YYYY-MM-DD upper bound on transport_date (inclusive). */
  dateTo: z.string().optional(),
  department: z.string().max(100).optional(),
  tripType: z.string().max(20).optional(),
  transportType: z.string().max(8).optional(),
  /** Filter to a triplink_status value (e.g. "assigned", "finished")
   *  or one of the pseudo-statuses matching the UI's status-group
   *  chips: "canceled" matches every cancellation variant, "pending"
   *  the whole waiting bucket (pending / available / draft / empty
   *  pre-sync), "in_progress" every on-the-road status. Empty = all. */
  status: z.string().max(32).optional(),
  /** When true (default false), exclude rows where canceled_at IS NOT
   *  NULL — the Admin → Bookings page defaults this on so the wall of
   *  cancelled trips doesn't drown out live ones. */
  hideCancelled: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => v === true || v === "true" || v === "1"),
  /** Filter by origin: "tms" = synced rows, "form" = V2 form rows.
   *  Useful for "show me only what came through phone/V1" or the
   *  reverse. Empty = all origins. */
  origin: z.enum(["tms", "form"]).optional(),
  /** When true, restrict to rows where bookings.mrn is empty —
   *  catches old TMS imports where the patient identifier wasn't
   *  captured. Surfaces orphan rows that the Members tab can't
   *  show (they aggregate to the empty-MRN bucket). */
  missingMrn: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => v === true || v === "true" || v === "1"),
  /** When true, restrict to "no-fare" trips: NON-cancelled, billable
   *  trips whose computed fare is still TBD/null (no usable pickup-time
   *  window, no override). Surfaces trips Stage 2 leaves unpriced —
   *  e.g. appointment-mode trips not yet backfilled with a scheduled
   *  pickup. Gated server-side behind `viewMileageRate` (a fare concept);
   *  ignored for requesters without it. */
  noFare: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => v === true || v === "true" || v === "1"),
  /** When true, restrict to trips where NEITHER the pickup NOR the dropoff
   *  is one of Mount Sinai's own locations — a trip that never touches the
   *  hospital.
   *
   *  Deliberately NOT "either end is off-site": a discharge is hospital ->
   *  the patient's home and an appointment is home -> hospital, so exactly
   *  one known endpoint is the shape of normal patient transport and that
   *  rule would flag nearly every booking. See `classifyTripEndpoints` in
   *  locationMatch.ts for the full reasoning.
   *
   *  Requested by Mount Sinai (2026-09-04) to review trips that may not be
   *  patient transport at all. A review aid, not an enforcement gate. */
  offSite: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => v === true || v === "true" || v === "1"),
  /** With `offSite`: the trips still to review (default), or the ones an
   *  admin has approved. */
  offSiteView: z.enum(["review", "approved"]).default("review"),
  createdByUserId: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  beforeCreatedAt: z.string().optional(),
  beforeId: z.string().optional(),
});
export type AdminBookingsQuery = z.infer<typeof adminBookingsQuerySchema>;

export const adminBookingsListResponseSchema = z.object({
  bookings: z.array(adminBookingSchema),
  hasMore: z.boolean(),
  nextCursor: z
    .object({
      createdAt: z.string(),
      id: z.string().uuid(),
    })
    .nullable(),
  /** Distinct departments seen — drives the filter dropdown. */
  departments: z.array(z.string()),
  /** Total number of rows that match the current filter set (cap at
   *  1,000 — past that we just say "1000+" client-side). */
  total: z.number().int().nonnegative(),
  /** How many NON-cancelled trips in the current filter set (period +
   *  filters, ignoring the `noFare` toggle itself) have no computed fare
   *  — the unpriced/TBD bucket. Drives the count badge on the "No-fare"
   *  filter pill. Null when the requester lacks `viewMileageRate` (fares
   *  aren't computed for them, so the bucket is undefined). */
  noFareCount: z.number().int().nonnegative().nullable(),
  /** How many trips in the current filter set (period + filters, ignoring
   *  the `offSite` toggle itself) have NEITHER endpoint at a Mount Sinai
   *  location and still need review: trips an admin approved are counted in
   *  `offSiteApprovedCount` instead. Drives the count badge on the "Off-site"
   *  filter pill.
   *
   *  Null when the bucket is UNDEFINED rather than empty — i.e. no active
   *  location carries a street address to compare against. Without that
   *  distinction an unconfigured Locations table would classify every trip
   *  in the system as off-site and the badge would read like an incident. */
  offSiteCount: z.number().int().nonnegative().nullable(),
  /** Off-site trips in the same set that an admin has approved. */
  offSiteApprovedCount: z.number().int().nonnegative().nullable().optional(),
});
export type AdminBookingsListResponse = z.infer<typeof adminBookingsListResponseSchema>;

/** Approving an off-site trip: why it's fine, for whoever looks next. */
export const offsiteApprovalRequestSchema = z.object({
  note: z.string().max(500, "Keep the note under 500 characters.").default("").transform((v) => v.trim()),
});
export type OffsiteApprovalRequest = z.infer<typeof offsiteApprovalRequestSchema>;

// =============================================================================
// Departments — admin-managed lookup
// =============================================================================

export const adminDepartmentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  defaultCostCenter: z.string(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  /** When true, the department is hidden from non-admin form dropdowns
   *  and a trip create against it is rejected server-side. Used for
   *  test / sandbox departments (e.g. "Royal Transportation" with
   *  cost center 0000) so QA can keep using them without polluting
   *  production data. */
  adminOnly: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminDepartment = z.infer<typeof adminDepartmentSchema>;

export const adminDepartmentsListResponseSchema = z.object({
  departments: z.array(adminDepartmentSchema),
});
export type AdminDepartmentsListResponse = z.infer<
  typeof adminDepartmentsListResponseSchema
>;

export const adminDepartmentCreateRequestSchema = z.object({
  name: z.string().min(1).max(100).transform((v) => v.trim()),
  defaultCostCenter: z.string().max(20).default("").transform((v) => v.trim()),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  /** Hide this department from non-admin form dropdowns. Default
   *  false so existing departments stay visible to everyone. */
  adminOnly: z.boolean().default(false),
});
export type AdminDepartmentCreateRequest = z.infer<
  typeof adminDepartmentCreateRequestSchema
>;

export const adminDepartmentUpdateRequestSchema = adminDepartmentCreateRequestSchema
  .partial()
  .refine((v) => Object.keys(v).length > 0, "Provide at least one field to update.");
export type AdminDepartmentUpdateRequest = z.infer<
  typeof adminDepartmentUpdateRequestSchema
>;

export const adminDepartmentResponseSchema = z.object({
  department: adminDepartmentSchema,
});
export type AdminDepartmentResponse = z.infer<typeof adminDepartmentResponseSchema>;

// ----- Unmatched-departments review queue --------------------------------
//
// Surfaces every distinct department string captured on a `bookings` row
// that doesn't match an entry in the `departments` table — typos, casing
// drift, free-text experiments. Each entry comes with the booking count
// that would be affected by an action plus deterministic fuzzy-match
// suggestions (Levenshtein distance against the curated list, no LLM
// involved — keeps everything inside the BAA boundary).

export const adminUnmatchedDepartmentSchema = z.object({
  /** The department string exactly as captured on the bookings rows. */
  rawName: z.string(),
  /** Lowercased + trimmed canonical key — used as the body parameter on
   *  the resolve endpoint so casing variants don't bypass dismissals. */
  normalizedName: z.string(),
  /** How many booking rows currently use this exact `rawName`. Affects
   *  the "Merge into …" UI ("this will rewrite N bookings"). */
  bookingCount: z.number().int().nonnegative(),
  /** Most common cost-center value across those bookings — pre-fills
   *  the Create Department dialog when an admin promotes the row. */
  mostCommonCostCenter: z.string(),
  /** Up to 3 existing department names ranked closest by string
   *  similarity. The frontend shows them as one-click "merge into"
   *  shortcuts. Empty when nothing in the curated list is close. */
  suggestedMatches: z.array(z.string()).default([]),
});
export type AdminUnmatchedDepartment = z.infer<
  typeof adminUnmatchedDepartmentSchema
>;

export const adminUnmatchedDepartmentsListResponseSchema = z.object({
  unmatched: z.array(adminUnmatchedDepartmentSchema),
});
export type AdminUnmatchedDepartmentsListResponse = z.infer<
  typeof adminUnmatchedDepartmentsListResponseSchema
>;

export const adminUnmatchedDepartmentResolveRequestSchema = z.discriminatedUnion(
  "action",
  [
    /** Promote the unmatched name into a real department row. The
     *  optional `canonicalName` lets the admin correct casing or
     *  spelling at promotion time (e.g. "cardilogy" → "Cardiology")
     *  without an extra round trip. When supplied, bookings with the
     *  old name are backfilled to use canonicalName. */
    z.object({
      action: z.literal("add"),
      rawName: z.string().min(1).max(200),
      canonicalName: z.string().min(1).max(100).optional(),
      defaultCostCenter: z.string().max(20).default(""),
    }),
    /** Merge the unmatched name into an existing department. Backfills
     *  bookings.department from rawName to the canonical name so the
     *  unmatched row disappears from the queue on next refresh. */
    z.object({
      action: z.literal("merge"),
      rawName: z.string().min(1).max(200),
      mergeIntoDepartmentId: z.string().uuid(),
    }),
    // No "dismiss" — every unmatched entry has to be either added as
    // a real department or merged into an existing one. Hiding without
    // resolving leaves the bookings with an uncurated department string
    // that splits future reports and silently drifts the data, which
    // is exactly the problem this queue is meant to surface. If
    // something is genuinely junk (typo, test data), the right tool is
    // /admin/bookings → Delete on the offending row.
  ],
);
export type AdminUnmatchedDepartmentResolveRequest = z.infer<
  typeof adminUnmatchedDepartmentResolveRequestSchema
>;

// =============================================================================
// Locations — admin-managed lookup, hierarchical (campus → buildings)
// =============================================================================

export const adminLocationSchema = z.object({
  id: z.string().uuid(),
  parentLocationId: z.string().uuid().nullable(),
  name: z.string(),
  addressLine: z.string(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  entranceLabel: z.string(),
  /** Other addresses the building goes by. Recognised by the off-site trip
   *  check; never shown in the trip form. */
  otherAddresses: z.array(z.string()),
  isPickup: z.boolean(),
  isDropoff: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminLocation = z.infer<typeof adminLocationSchema>;

/** One "other address" line, e.g. "4306 Alton Rd, Miami Beach, FL 33140".
 *  It has to be a line the off-site matcher can use (a building number and a
 *  street); anything else would look saved while recognising nothing. Blank
 *  lines are dropped, not rejected, so an empty row left in the form doesn't
 *  block Save. */
export const locationOtherAddressesSchema = z
  .array(z.string().max(200).transform((v) => v.trim().replace(/\s+/gu, " ")))
  .max(10, "A location can have at most 10 other addresses.")
  .transform((lines) => [...new Set(lines.filter(Boolean))])
  .superRefine((lines, ctx) => {
    for (const line of lines) {
      if (!isMatchableAddress(line)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `"${line}" needs a building number and a street, like "4306 Alton Rd, Miami Beach, FL 33140". Put each building number on its own line.`,
        });
      }
    }
  });

export const adminLocationsListResponseSchema = z.object({
  locations: z.array(adminLocationSchema),
});
export type AdminLocationsListResponse = z.infer<
  typeof adminLocationsListResponseSchema
>;

/** Refine that forces city + state + postal once an address line is
 *  set. Without this the trip form picks the location, gets an empty
 *  `cityZip` back, and immediately falls back to the manual
 *  "Pick-up city, state ZIP" prompt — surprising the requester right
 *  after they picked a known Mount Sinai location. Catching it at
 *  the schema level means both the admin form and any direct API
 *  caller (e.g. a future import script) have to do this right. */
function requireFullAddressWhenLineSet(
  v: {
    addressLine?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    lat?: number | null;
    lon?: number | null;
  },
  ctx: z.RefinementCtx,
) {
  const line = (v.addressLine ?? "").trim();
  if (!line) return;
  // When the location carries map coordinates we send exact lat/lon to
  // dispatch, and the trip form no longer asks the requester for
  // city/ZIP for coordinate-backed locations — so the structured
  // city/state/ZIP aren't required here either. (They're still required
  // for a street address with no pin, where routing needs them.)
  const hasCoords =
    v.lat != null &&
    v.lon != null &&
    Number.isFinite(v.lat) &&
    Number.isFinite(v.lon);
  if (hasCoords) return;
  const missing: Array<"city" | "state" | "postalCode"> = [];
  if (!(v.city ?? "").trim()) missing.push("city");
  if (!(v.state ?? "").trim()) missing.push("state");
  if (!(v.postalCode ?? "").trim()) missing.push("postalCode");
  for (const field of missing) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [field],
      message:
        "City, state, and ZIP are all required once a street address is set — pick the address from the suggestions to capture them automatically.",
    });
  }
}

export const adminLocationCreateRequestSchema = z
  .object({
    parentLocationId: z.string().uuid().nullable().default(null),
    name: z.string().min(1).max(200).transform((v) => v.trim()),
    addressLine: z.string().max(200).default("").transform((v) => v.trim()),
    city: z.string().max(100).default("").transform((v) => v.trim()),
    state: z.string().max(2).default("").transform((v) => v.trim().toUpperCase()),
    postalCode: z.string().max(10).default("").transform((v) => v.trim()),
    lat: z.number().nullable().default(null),
    lon: z.number().nullable().default(null),
    entranceLabel: z.string().max(200).default("").transform((v) => v.trim()),
    otherAddresses: locationOtherAddressesSchema.default([]),
    isPickup: z.boolean().default(true),
    isDropoff: z.boolean().default(true),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().default(0),
  })
  .superRefine(requireFullAddressWhenLineSet);
export type AdminLocationCreateRequest = z.infer<
  typeof adminLocationCreateRequestSchema
>;

// PATCH-shaped: every field optional, plus at-least-one-field required.
// The full-address invariant lives on the handler instead of this
// schema — for a partial update we need to check the *merged* state
// (existing row + this patch), which the schema doesn't have access
// to. See admin.ts → patch /locations/:id.
export const adminLocationUpdateRequestSchema = z
  .object({
    parentLocationId: z.string().uuid().nullable().optional(),
    name: z.string().min(1).max(200).transform((v) => v.trim()).optional(),
    addressLine: z.string().max(200).transform((v) => v.trim()).optional(),
    city: z.string().max(100).transform((v) => v.trim()).optional(),
    state: z.string().max(2).transform((v) => v.trim().toUpperCase()).optional(),
    postalCode: z.string().max(10).transform((v) => v.trim()).optional(),
    lat: z.number().nullable().optional(),
    lon: z.number().nullable().optional(),
    entranceLabel: z.string().max(200).transform((v) => v.trim()).optional(),
    otherAddresses: locationOtherAddressesSchema.optional(),
    isPickup: z.boolean().optional(),
    isDropoff: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, "Provide at least one field to update.");
export type AdminLocationUpdateRequest = z.infer<
  typeof adminLocationUpdateRequestSchema
>;

export const adminLocationResponseSchema = z.object({
  location: adminLocationSchema,
});
export type AdminLocationResponse = z.infer<typeof adminLocationResponseSchema>;

// =============================================================================
// Members — distinct patients aggregated from the bookings table.
//
// "Member" is Mount Sinai's term for a patient. The list is keyed on
// MRN (the only patient identifier that's reliably populated across
// every booking channel — V1 form, V2 form, phone via triplink sync).
// Each row is an aggregation, not a row from the `patients` table:
//   * V2-form patients exist in `patients` with verified DOBs.
//   * V1 / phone-booked patients only exist in `bookings` after the
//     sync imports them.
//
// Aggregating from `bookings` gives us a unified directory regardless
// of origin. The `patients` table is now used mainly for V2's
// verified-DOB lookup gate; member listings live here.
// =============================================================================

export const adminMemberSchema = z.object({
  mrn: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  /** Most recent department recorded on a booking for this patient.
   *  Empty for synced-only members (no department in /schedule). */
  lastDepartment: z.string(),
  /** Lifetime booking count across all channels. */
  tripCount: z.number().int().nonnegative(),
  /** Date of the most recent trip (transport_date, ISO YYYY-MM-DD).
   *  Null if every row has a null transport_date. */
  lastTripDate: z.string().nullable(),
  /** When this patient first appeared in any of our tables. */
  firstSeenAt: z.string(),
  /** Mix of trip origins for this member. Useful for the small
   *  badge — "TMS only", "FORM only", "MIXED". */
  hasFormTrips: z.boolean(),
  hasTmsTrips: z.boolean(),
  /** Server-side cleanup detection — true when any of:
   *    * first or last name differs from its title-cased form
   *    * the MRN has 2+ distinct (first, last) tuples across bookings
   *    * the MRN has 2+ distinct DOBs across bookings (possible
   *      re-issued MRN; the patients table is keyed on (mrn, dob))
   *
   *  Client uses the count fields below to render a per-row tooltip
   *  explaining *which* patterns matched. */
  needsCleanup: z.boolean(),
  /** Count of distinct (lowercased first, lowercased last) tuples
   *  observed across this MRN's bookings. >1 means dispatch / triplink
   *  has the patient on file under multiple spellings. */
  nameVariantsCount: z.number().int().nonnegative(),
  /** Count of distinct DOBs observed across this MRN's bookings (null
   *  DOBs excluded — synced-only rows don't carry DOB). >1 hints at a
   *  re-issued MRN or a data-entry mismatch. */
  dobVariantsCount: z.number().int().nonnegative(),
  /** Actual (firstName, lastName) variants observed across this MRN's
   *  bookings. The UI renders this inline as a "Spellings: …" line so
   *  admins can see *which* spellings are on file without opening the
   *  trip history. Always returned; only rendered when the count is
   *  >1. Empty pairs (first='' AND last='') are excluded. */
  nameVariants: z
    .array(z.object({ firstName: z.string(), lastName: z.string() }))
    .default([]),
  /** Distinct DOBs observed across this MRN's bookings, ISO format
   *  (`YYYY-MM-DD`). Rendered inline next to the "X DOBs" pill to
   *  show admins the conflicting dates. */
  dobVariants: z.array(z.string()).default([]),
  /** MRN doesn't match the canonical shape (digits only, any
   *  length). Catches our "MRN-0001" test data, anything pasted in
   *  with stray characters, or an agent typing a name into the MRN
   *  field by accident. No length cap on purpose — real MRNs vary
   *  in length and the earlier 6-10 ceiling was an assumption I
   *  made, not a Mount Sinai spec. */
  mrnLooksOff: z.boolean(),
  /** At least one of firstName / lastName is empty. Distinct from
   *  "casing differs" because casing-check passes for empty strings. */
  partialName: z.boolean(),
  /** First or last name contains digits. Almost always a data-entry
   *  error (e.g. someone typed an MRN into the name field). */
  nameHasDigits: z.boolean(),
  /** At least one booking for this MRN has a DOB in the future or
   *  more than 110 years in the past. Catches typos like
   *  1936→2036 and "01/01/0019". TMS-synced rows don't surface DOB
   *  so they never trigger this. */
  hasInvalidDob: z.boolean(),
  /** First or last name has the same word repeated ("Alfonso M
   *  Alfonso M. Aparcana Mendoza"). Common data-entry slip from
   *  pasting the same value twice. */
  nameHasDuplicateWords: z.boolean(),
  /** At least one V2-form booking has a contact or callback phone
   *  number that isn't a 10-digit US number (after stripping
   *  punctuation). TMS-synced rows store nulls and don't trigger. */
  hasInvalidPhone: z.boolean(),
  /** Contact phone from this member's most recent booking, verbatim —
   *  including a malformed one. Prefills the phone editor so an admin
   *  clearing `hasInvalidPhone` can SEE the bad value they are fixing
   *  rather than retyping from memory. Empty when no booking carries one.
   *  Default-ed so an older API response can't fail validation. */
  contactPhone: z.string().default(""),
});
export type AdminMember = z.infer<typeof adminMemberSchema>;

export const adminMembersQuerySchema = z.object({
  /** Free-text search across MRN + first name + last name. */
  search: z.string().max(200).optional(),
  /** Origin filter chip — "tms" surfaces members we only know from
   *  the sync, "form" surfaces members who've booked through V2 at
   *  least once. */
  origin: z.enum(["tms", "form"]).optional(),
  /** When true, restrict to rows the server's needs_cleanup detector
   *  flagged — see the field comment on adminMemberSchema for the
   *  exact patterns. Used by the "Review needed" filter chip. */
  needsCleanup: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => v === true || v === "true" || v === "1"),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** Cursor: (last_trip_date, mrn) tuple — older trips paginate
   *  forward. Same shape as bookings cursor but on a different sort. */
  beforeLastTrip: z.string().optional(),
  beforeMrn: z.string().optional(),
});
export type AdminMembersQuery = z.infer<typeof adminMembersQuerySchema>;

export const adminMembersListResponseSchema = z.object({
  members: z.array(adminMemberSchema),
  hasMore: z.boolean(),
  nextCursor: z
    .object({ lastTripDate: z.string(), mrn: z.string() })
    .nullable(),
  total: z.number().int().nonnegative(),
});
export type AdminMembersListResponse = z.infer<
  typeof adminMembersListResponseSchema
>;

// =============================================================================
// Triplink sync — admin-only trigger for the background reconciliation
// pass. Normally runs on a 5-minute timer inside the API; the manual
// endpoint exists so an operator can force a refresh from the admin
// panel or kick off a one-time historical backfill on a fresh deploy.
// =============================================================================

/** Hard cap on the custom backfill window (days back from today). The
 *  backfill does ONE upstream feed fetch per date, so a huge range is
 *  heavy — and the sync service already clamps a pass to its own
 *  `MAX_BACKFILL_DAYS` (120) via `clampDayCount`, so anything larger here
 *  would be silently clamped server-side anyway. 120 (~4 months) amply
 *  covers the "repopulate the last N days of trips" cases (e.g. filling
 *  the new `scheduled_pickup_time` column) without inventing a wider
 *  window than the sync can honour. Shared so the UI and the server
 *  enforce the exact same ceiling. */
export const MAX_SYNC_BACKFILL_DAYS = 120;

export const adminSyncTriplinkRequestSchema = z.object({
  /** Preferred custom-backfill control: how many days back from today to
   *  re-scan. The pass covers `[today - days, today + forwardDays]`,
   *  reusing the same windowing the scheduled tick uses. Drives the
   *  custom-days backfill in the internal TripLink Sync UI. Integer
   *  1..120; takes precedence over `backwardDays` when both are sent. */
  days: z.number().int().min(1).max(MAX_SYNC_BACKFILL_DAYS).optional(),
  /** Override the default backward window. Bump this on first deploy
   *  (e.g. 60-90) to backfill historical V1 / phone bookings. Kept for
   *  back-compat; `days` is the preferred field for the UI backfill. */
  backwardDays: z.number().int().min(0).max(120).optional(),
  /** Override the default 14-day forward window. Rarely needed; the
   *  default is plenty for duplicate detection. */
  forwardDays: z.number().int().min(0).max(120).optional(),
});
export type AdminSyncTriplinkRequest = z.infer<
  typeof adminSyncTriplinkRequestSchema
>;

export const adminSyncTriplinkStatsSchema = z.object({
  tripsSeen: z.number(),
  inserted: z.number(),
  updated: z.number(),
  cancelledMarked: z.number(),
  failedDates: z.array(z.string()),
  startDate: z.string(),
  endDate: z.string(),
  durationMs: z.number(),
});
export type AdminSyncTriplinkStats = z.infer<
  typeof adminSyncTriplinkStatsSchema
>;

export const adminSyncTriplinkResponseSchema = z.object({
  stats: adminSyncTriplinkStatsSchema,
});
export type AdminSyncTriplinkResponse = z.infer<
  typeof adminSyncTriplinkResponseSchema
>;

/** Compact counts from the most recent FINISHED sync pass, lifted from
 *  its audit row. Drives the internal indicator's "Synced N · M new · K
 *  updated" finished line. */
export const adminSyncTriplinkLastRunSchema = z.object({
  /** When the pass finished (its audit row's timestamp), ISO UTC. */
  finishedAt: z.string(),
  /** "success" (clean) or "partial" (some dates failed upstream). */
  outcome: z.string(),
  tripsSeen: z.number().int().nonnegative(),
  inserted: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  /** How many dates triplink failed on during the pass (0 = clean). */
  failedDates: z.number().int().nonnegative(),
});
export type AdminSyncTriplinkLastRun = z.infer<
  typeof adminSyncTriplinkLastRunSchema
>;

export const adminSyncTriplinkStatusResponseSchema = z.object({
  /** A sync pass is holding the cross-instance advisory lock RIGHT NOW.
   *  Reflects the real lock state regardless of who started the pass —
   *  the 5-minute scheduler on any instance, or another admin's manual
   *  run / backfill — not just locally-initiated runs. Drives the
   *  "Sync in progress…" indicator + the busy state on the action
   *  buttons. */
  running: z.boolean(),
  /** ISO timestamp of the most recent sync that touched any local row,
   *  or null if the sync has never run yet. The admin UI converts this
   *  to a "synced X min ago" chip. */
  lastSyncedAt: z.string().nullable(),
  /** The most recent FINISHED pass's summary (counts + outcome), or null
   *  when no pass has completed yet. Used for the indicator's finished /
   *  result line. */
  lastRun: adminSyncTriplinkLastRunSchema.nullable(),
});
export type AdminSyncTriplinkStatusResponse = z.infer<
  typeof adminSyncTriplinkStatusResponseSchema
>;

// ----- Single-trip on-demand re-sync ---------------------------------------
//
// Operator action inside the internal TripLink Sync module: re-pull and
// reconcile ONE trip by its friendly id, so a dispatcher's late
// service-type / mileage / status correction in TripLink lands in the
// portal immediately instead of waiting for the next 5-minute tick.
// Reuses the same reconcileTrip the scheduled pass runs.

export const adminResyncTripRequestSchema = z.object({
  /** TripLink friendly id of the trip to re-sync (e.g. "260600003953-A").
   *  Trimmed + upper-cased server-side before matching. */
  friendlyId: z
    .string()
    .trim()
    .min(1, "Enter a trip / friendly ID.")
    .max(64),
});
export type AdminResyncTripRequest = z.infer<
  typeof adminResyncTripRequestSchema
>;

/** One billing-relevant field that changed on the re-synced trip. */
export const adminResyncTripChangeSchema = z.object({
  /** Column that moved: "transport_type" | "mileage" | "status" |
   *  "transport_date" | "canceled_at". */
  field: z.string(),
  /** Stored value before the re-sync (JSON-serialized, may be null). */
  from: z.unknown(),
  /** Value written by the re-sync. */
  to: z.unknown(),
});
export type AdminResyncTripChange = z.infer<typeof adminResyncTripChangeSchema>;

export const adminResyncTripResponseSchema = z.object({
  friendlyId: z.string(),
  /** Dates pulled from TripLink while locating the trip. */
  datesSearched: z.array(z.string()),
  /** True when TripLink's feed carried the trip on a searched date. */
  found: z.boolean(),
  /** True when a local booking row already existed for the friendly id. */
  existedLocally: z.boolean(),
  /** reconcileTrip's verdict — omitted when the trip wasn't found upstream. */
  outcome: z.string().optional(),
  /** Billing-relevant fields that changed, old→new. Empty when the trip
   *  was already up to date (or wasn't found). */
  changes: z.array(adminResyncTripChangeSchema),
});
export type AdminResyncTripResponse = z.infer<
  typeof adminResyncTripResponseSchema
>;

// =============================================================================
// Public meta directory (any signed-in user). Trimmed to what the
// trip-request form actually needs — name + cost center for departments,
// label + address + lat/lon + flags for locations.
// =============================================================================

export const metaDepartmentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  defaultCostCenter: z.string(),
  /** True when the row is gated to admin / super_admin users only.
   *  Regular users never receive admin-only rows from /api/meta/
   *  departments (server filters them out), so this flag will only
   *  ever be `true` on the wire for admin callers. The frontend uses
   *  it to render a small "admin only" badge next to the department
   *  name in the picker so admins can tell at a glance which entries
   *  are sandbox / restricted. */
  adminOnly: z.boolean().default(false),
});
export type MetaDepartment = z.infer<typeof metaDepartmentSchema>;

export const metaDepartmentsResponseSchema = z.object({
  departments: z.array(metaDepartmentSchema),
});
export type MetaDepartmentsResponse = z.infer<typeof metaDepartmentsResponseSchema>;

export const metaLocationSchema = z.object({
  id: z.string().uuid(),
  parentLocationId: z.string().uuid().nullable(),
  name: z.string(),
  addressLine: z.string(),
  cityZip: z.string(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  entranceLabel: z.string(),
  isPickup: z.boolean(),
  isDropoff: z.boolean(),
});
export type MetaLocation = z.infer<typeof metaLocationSchema>;

export const metaLocationsResponseSchema = z.object({
  locations: z.array(metaLocationSchema),
});
export type MetaLocationsResponse = z.infer<typeof metaLocationsResponseSchema>;

// =============================================================================
// TMS reconciliation queue
//
// Read + resolve endpoints for the queue Mount Sinai admins generate
// by editing patient identity data in MSMC Portal. Every queue item
// represents "this record needs the same correction in TripLink's
// TMS." Royal team works through the list and marks rows resolved.
// =============================================================================

/**
 * The operations team responsible for reconciling MSMC Portal cleanup
 * back into TMS sit in this department. The TMS Cleanup surface (nav
 * tab, page, dashboard tile, and the read / resolve API endpoints) is
 * scoped to members of this department so Mount Sinai admins aren't
 * shown a to-do list that isn't theirs to act on.
 *
 * Mount Sinai's own admins sit in clinical / operational departments
 * (e.g. "Safety"), so the department string cleanly separates the two
 * audiences. Compared case-insensitively against `users.department`.
 */
export const ROYAL_TEAM_DEPARTMENT = "Royal Transportation";

/** True when a user's department marks them as part of the Royal /
 *  TMS operations team. Case- and whitespace-insensitive. */
export function isRoyalTeamDepartment(
  department: string | null | undefined,
): boolean {
  return (
    (department ?? "").trim().toLowerCase() ===
    ROYAL_TEAM_DEPARTMENT.toLowerCase()
  );
}

export const tmsReconciliationActionSchema = z.enum([
  "rename",
  "rekey",
  "dob_change",
  "phone_change",
  "merge",
  "delete",
]);
export type TmsReconciliationAction = z.infer<
  typeof tmsReconciliationActionSchema
>;

export const tmsReconciliationStatusSchema = z.enum([
  "pending",
  "resolved",
  "dismissed",
  // Auto-applied when a newer change to the same patient replaces a
  // still-pending item — keeps the actionable queue to one row per
  // patient. Superseded rows stay for history but aren't surfaced in
  // the queue page's pending / resolved / dismissed views.
  "superseded",
]);
export type TmsReconciliationStatus = z.infer<
  typeof tmsReconciliationStatusSchema
>;

export const tmsReconciliationItemSchema = z.object({
  id: z.string().uuid(),
  mrn: z.string(),
  priorMrn: z.string().nullable(),
  action: tmsReconciliationActionSchema,
  /** Free-form snapshot — shape varies by action. UI surfaces the
   *  before/after fields most relevant to the action type. */
  details: z.record(z.unknown()).default({}),
  createdAt: z.string(),
  createdByUserId: z.string().uuid().nullable(),
  createdByName: z.string(),
  createdByEmail: z.string(),
  resolvedAt: z.string().nullable(),
  resolvedByUserId: z.string().uuid().nullable(),
  resolvedByName: z.string(),
  resolvedNote: z.string(),
  status: tmsReconciliationStatusSchema,
});
export type TmsReconciliationItem = z.infer<typeof tmsReconciliationItemSchema>;

export const tmsReconciliationListQuerySchema = z.object({
  /** Filter by status. Default = pending only (the cleanup queue
   *  the page is for). */
  status: tmsReconciliationStatusSchema.optional(),
  /** Filter to a single MRN — useful from the Members tab to see
   *  outstanding reconciliation for one patient. */
  mrn: z.string().optional(),
  /** Cursor + page size — standard pattern. */
  limit: z.coerce.number().int().min(1).max(200).default(50),
  beforeCreatedAt: z.string().optional(),
  beforeId: z.string().uuid().optional(),
});
export type TmsReconciliationListQuery = z.infer<
  typeof tmsReconciliationListQuerySchema
>;

export const tmsReconciliationListResponseSchema = z.object({
  items: z.array(tmsReconciliationItemSchema),
  hasMore: z.boolean(),
  nextCursor: z
    .object({ createdAt: z.string(), id: z.string().uuid() })
    .nullable(),
  /** Aggregated counts driving the Reports dashboard tile + the
   *  top-of-page summary chips on the queue page. */
  counts: z.object({
    pending: z.number().int().nonnegative(),
    resolved: z.number().int().nonnegative(),
    dismissed: z.number().int().nonnegative(),
  }),
});
export type TmsReconciliationListResponse = z.infer<
  typeof tmsReconciliationListResponseSchema
>;

export const tmsReconciliationResolveRequestSchema = z.object({
  /** 'resolved' — Royal team fixed the upstream record.
   *  'dismissed' — explicitly skipped (e.g. patient deceased,
   *  no further action needed). */
  status: z.enum(["resolved", "dismissed"]),
  /** Optional free-text note from the resolver. */
  note: z.string().max(500).default(""),
});
export type TmsReconciliationResolveRequest = z.infer<
  typeof tmsReconciliationResolveRequestSchema
>;

export const tmsReconciliationResolveResponseSchema = z.object({
  item: tmsReconciliationItemSchema,
});
export type TmsReconciliationResolveResponse = z.infer<
  typeof tmsReconciliationResolveResponseSchema
>;

// =============================================================================
// Reports → Operations dashboard
//
// Single fat endpoint that returns every KPI / anomaly / breakdown the
// Admin → Reports landing page needs. We deliberately don't split into
// micro-endpoints (one per tile) — the dashboard is rendered as a unit,
// caching is easier with one query, and the constituent queries share
// date-window arithmetic. The page polls this every 5 minutes for
// auto-refresh.
//
// Time windows are computed server-side so client clock skew can't
// quietly shift "this week". They're rolling (last 7 / 14 days from
// now()) rather than calendar-week so Monday-morning loads aren't
// suddenly empty.
// =============================================================================

/** A "this period vs prior period" pair, used by the KPI cards. */
export const reportsKpiDeltaSchema = z.object({
  /** Count in the current window (last 7 days). */
  current: z.number().int().nonnegative(),
  /** Count in the prior window (the 7 days before that). */
  prior: z.number().int().nonnegative(),
  /** `current - prior`. Negative means down vs prior. */
  delta: z.number().int(),
  /** `(current - prior) / prior * 100`, rounded. Null when `prior` is 0
   *  (we'd be dividing by zero — render the delta absolute instead). */
  deltaPct: z.number().int().nullable(),
});
export type ReportsKpiDelta = z.infer<typeof reportsKpiDeltaSchema>;

/** Triplink sync health rollup. `state` is the traffic-light value the
 *  dashboard renders; minutes-since-sync is exposed separately so the
 *  client can show "synced X min ago" without re-parsing the timestamp. */
export const reportsSyncHealthSchema = z.object({
  /** Most recent sync timestamp across any local row, ISO UTC. Null
   *  when the sync has never run (e.g. fresh deploy before the timer
   *  fires for the first time). */
  lastSyncedAt: z.string().nullable(),
  /** Whole minutes since `lastSyncedAt`. Null when the sync has never
   *  run. */
  minutesSinceSync: z.number().int().nullable(),
  /** Server-derived traffic light. Thresholds are intentionally
   *  generous because the on-instance timer fires every 5 minutes
   *  and a missed tick is normal during a redeploy. Crossing 60
   *  minutes is the real "something's wrong" line. */
  state: z.enum(["ok", "stale", "never"]),
});
export type ReportsSyncHealth = z.infer<typeof reportsSyncHealthSchema>;

/** A simple "label + count" entry used by every breakdown panel.
 *  `id` is optional — set only where a row deep-links to a filtered
 *  view by a stable identifier rather than its display label (e.g.
 *  Top bookers carries the user id so the panel can link to that
 *  user's bookings). */
export const reportsBreakdownEntrySchema = z.object({
  label: z.string(),
  count: z.number().int().nonnegative(),
  id: z.string().optional(),
});
export type ReportsBreakdownEntry = z.infer<typeof reportsBreakdownEntrySchema>;

export const reportsOperationsDashboardResponseSchema = z.object({
  /** ISO timestamp this dashboard was generated. Lets the UI render
   *  "Updated X seconds ago" alongside an auto-refresh tick. */
  generatedAt: z.string(),
  /** The date windows used to compute the metrics, surfaced so the UI
   *  can render an "explainer" line ("Last 7 days: May 20 → May 26"). */
  windows: z.object({
    today: z.string(), // YYYY-MM-DD (UTC)
    last7DaysStart: z.string(), // YYYY-MM-DD (UTC)
    prior7DaysStart: z.string(), // YYYY-MM-DD (UTC)
  }),
  /** Headline-row KPIs, the four big numbers at the top. */
  kpis: z.object({
    /** Bookings whose `created_at` lands in the current window. */
    tripsCreated: reportsKpiDeltaSchema,
    /** Distinct MRNs whose *first ever* `created_at` lands in the
     *  current window — first-time patients seen. */
    newMembers: reportsKpiDeltaSchema,
    /** Bookings whose `transport_date` is today. Not a delta — we
     *  surface it as a single number with a status breakdown. */
    tripsToday: z.object({
      total: z.number().int().nonnegative(),
      /** Map of `triplink_status` → count. Keys are whatever statuses
       *  exist on today's rows; UI groups them per the same
       *  StatusGroup mapping the Bookings page uses. */
      byStatus: z.record(z.number().int().nonnegative()),
    }),
    syncHealth: reportsSyncHealthSchema,
  }),
  /** Attention-grabbing tiles. Each carries a count and a click
   *  destination (encoded by the UI, not the server, so this stays a
   *  pure data payload). */
  anomalies: z.object({
    /** Pending invitations whose `expires_at` is within 48 hours and
     *  haven't been accepted or revoked. Sized to surface the "13
     *  expired invites" pattern Zakia reported during launch week
     *  one rollover earlier. */
    invitationsExpiringSoon: z.number().int().nonnegative(),
    /** Members the cleanup detector would surface — see Members tab. */
    membersNeedingCleanup: z.number().int().nonnegative(),
    /** Bookings whose `mrn` is empty (old TMS imports without an
     *  upstream identifier). The Bookings page surfaces these via
     *  the ⚠ Missing MRN chip; this is the same count. */
    bookingsMissingMrn: z.number().int().nonnegative(),
    /** Cancellations (current window). Paired with `cancellationRate`
     *  so the UI can render "12 cancellations (8% of created)". */
    cancellationsThisPeriod: z.number().int().nonnegative(),
    /** Cancellations as a percentage of `kpis.tripsCreated.current`,
     *  rounded. Null when no trips were created in the window
     *  (rate is undefined). */
    cancellationRatePct: z.number().int().nullable(),
    /** Distinct raw `bookings.department` values that don't match any
     *  curated row in the departments table — same definition Admin →
     *  Departments → Unmatched uses. Surfaced here so admins can
     *  resolve drift before it spreads. */
    unmatchedDepartments: z.number().int().nonnegative(),
    /** Bookings whose triplink status is still `will_call` even
     *  though the transport_date has already passed — the day went
     *  by and nobody activated the Will Call. This is the failure
     *  mode that stranded a patient at the Cancer Center on
     *  2026-05-13: the booking captured Will Call correctly but the
     *  activation button never got clicked, so dispatch never knew
     *  to send a driver back. Surfacing the count here lets admins
     *  catch the same pattern before it repeats. */
    pendingWillCallActivations: z.number().int().nonnegative(),
    /** Open TMS reconciliation items — patient-identity edits made in
     *  MSMC Portal that need a matching fix in TripLink's TMS. Each
     *  one represents an action the Royal team should take on their
     *  side to keep the two systems in sync. Surfaced here so the
     *  count is visible at the same place as everything else that
     *  needs attention. */
    tmsReconciliationPending: z.number().int().nonnegative(),
  }),
  /** Mid-density breakdown panels. Capped server-side so the payload
   *  stays small (top 5 / 10 each). */
  breakdowns: z.object({
    /** Top departments by trip count over the current window. */
    topDepartments: z.array(reportsBreakdownEntrySchema).max(10),
    /** trip_type → count over the current window. */
    tripTypeMix: z.array(reportsBreakdownEntrySchema).max(10),
    /** transport_type → count over the current window. */
    transportTypeMix: z.array(reportsBreakdownEntrySchema).max(10),
    /** Top portal users by bookings created in the current window —
     *  "who's doing the booking". Label is the user's display name (or
     *  email fallback); TMS/phone trips (no portal user) are excluded.
     *  Capped at the top 10. */
    topBookers: z.array(reportsBreakdownEntrySchema).max(10),
    /** Where the window's bookings originated — "MSMC Portal" (entered
     *  through this app) vs "TMS" (synced from TripLink: legacy form +
     *  phone). An adoption signal: how much volume the portal is
     *  capturing vs the legacy channel. */
    sourceMix: z.array(reportsBreakdownEntrySchema).max(10),
    /** Most-frequent pickup addresses over the window. Destinations are
     *  almost always the hospital, so pickups are the interesting axis
     *  (where patients are coming from). Top 10, raw address strings. */
    topPickups: z.array(reportsBreakdownEntrySchema).max(10),
    /** Bookings created per day for the last 14 days, oldest first.
     *  Drives the sparkline chart at the top of the dashboard so the
     *  trend is visible even when a single delta number looks flat. */
    dailyVolume: z.array(
      z.object({
        date: z.string(), // YYYY-MM-DD (UTC)
        count: z.number().int().nonnegative(),
      }),
    ),
  }),
  /** Efficiency stats rendered as small stat boxes. */
  operational: z.object({
    /** Average lead time in days between when a booking is created and
     *  the trip's transport date, over the current window. How far
     *  ahead trips are typically booked — useful for dispatch
     *  planning. Null when no datable bookings exist in the window. */
    avgLeadTimeDays: z.number().nullable(),
    /** How many bookings the lead-time average is based on. */
    leadTimeSampleSize: z.number().int().nonnegative(),
    /** Median wall-clock seconds a requester spent filling out the
     *  booking form before submitting (form open → submit), over the
     *  current window. Median (not mean) + outlier clamping keep a
     *  left-open tab from skewing it. Null when we have no timed
     *  submissions yet. Only single (non-recurring) portal bookings
     *  are timed. */
    medianBookingSeconds: z.number().int().nullable(),
    /** How many timed submissions the median is based on. */
    bookingSampleSize: z.number().int().nonnegative(),
  }),
  /** Deeper operational signals — bottom-of-dashboard section. Each
   *  field is its own sub-card; they're grouped here so the schema
   *  stays a single payload but the UI can render them as a "Insights"
   *  band separate from the at-a-glance KPIs. */
  insights: z.object({
    /** Highest-utilization members by completed (non-cancelled) trip
     *  count over the last 30 days. Excludes the unidentified no-MRN
     *  bucket. `id` carries the MRN so the panel can deep-link to that
     *  member's bookings; `label` is the member's most-recent name. */
    topMembers: z.array(reportsBreakdownEntrySchema).max(10),
    /** Cancellations grouped by triplink status variant
     *  (customer_canceled / member_canceled / br_canceled / etc.),
     *  last 30 days. Top 10. */
    cancellationsByReason: z.array(reportsBreakdownEntrySchema).max(10),
    /** Bookings created per day-of-week (Mon-Sun), last 30 days.
     *  Drives the activity chart so admins can spot peak weekdays.
     *  Always returns 7 entries (one per day) so the UI can render
     *  a stable bar grid. */
    activityByDayOfWeek: z.array(
      z.object({
        /** 0 = Monday, 6 = Sunday. ISO numbering. */
        dow: z.number().int().min(0).max(6),
        label: z.string(),
        count: z.number().int().nonnegative(),
      }),
    ),
    /** Will Call activation health, last 8 weeks of *past* will-call
     *  trips (transport date already gone, cancellations excluded).
     *  "Activated" = the trip's status moved out of `will_call` —
     *  someone clicked Activate and dispatch sent a driver. The rate
     *  trending down is the early-warning for the stranded-patient
     *  failure mode (Cancer Center, 2026-05-13). */
    willCall: z.object({
      /** Past, non-cancelled will-call trips in the window. */
      totalPast: z.number().int().nonnegative(),
      activated: z.number().int().nonnegative(),
      /** activated / totalPast, rounded. Null when totalPast = 0. */
      overallRatePct: z.number().int().nullable(),
      /** Weekly buckets (by transport date, oldest first). */
      weeks: z.array(
        z.object({
          weekStart: z.string(), // YYYY-MM-DD (Monday)
          total: z.number().int().nonnegative(),
          activated: z.number().int().nonnegative(),
        }),
      ),
    }),
    /** Trips by pickup hour of day (wall-clock), last 30 days. Always
     *  24 entries (0-23), zero-filled, so the UI renders a stable
     *  hour grid. Counts trips with a parseable pickup/appointment
     *  time — Will Calls (no scheduled time) are excluded. */
    busiestHours: z.array(
      z.object({
        hour: z.number().int().min(0).max(23),
        count: z.number().int().nonnegative(),
      }),
    ),
    /** MRN format compliance trend — per day for the last 14 days.
     *  `valid` counts bookings whose MRN matches `^[0-9]+$`,
     *  `invalid` counts the rest (including empty). The dashboard
     *  renders the trend as a stacked sparkline so a sudden drop in
     *  compliance is obvious. */
    mrnComplianceTrend: z.array(
      z.object({
        date: z.string(), // YYYY-MM-DD (UTC)
        valid: z.number().int().nonnegative(),
        invalid: z.number().int().nonnegative(),
      }),
    ),
  }),
  /** Transportation-spend rollup (what MSMC pays Royal) over the
   *  current window, keyed by transport_date
   *  (service date, not booking date). Charges follow the billing
   *  rules: fare + IT fee per trip, IT-fee-only for cancellations,
   *  nothing for tombstoned rows. GATED: null when the requester lacks
   *  the `viewMileageRate` permission — pricing data never reaches a
   *  browser that shouldn't have it. */
  billing: z
    .object({
      /** Sum of grand totals (fare + fees) for the window. */
      totalCents: z.number().int().nonnegative(),
      /** Fare portion of the total. */
      fareCents: z.number().int().nonnegative(),
      /** IT-fee portion of the total. */
      itFeesCents: z.number().int().nonnegative(),
      /** Equal-length prior window's grand total — drives the delta. */
      priorTotalCents: z.number().int().nonnegative(),
      /** Trips contributing to the total (incl. fee-only cancellations). */
      billedTrips: z.number().int().nonnegative(),
      /** Live trips that can't be priced yet (no mileage or no clock
       *  pickup time) — the "TBD" bucket. Not in the total. */
      unpricedTrips: z.number().int().nonnegative(),
      /** Cancelled trips billed the IT fee only. */
      cancelledFeeOnlyTrips: z.number().int().nonnegative(),
      /** Total miles across fare-billed (non-cancelled) trips. */
      billedMiles: z.number().nonnegative(),
      /** Spend by transport code, sorted by spend desc. */
      byServiceType: z.array(
        z.object({
          label: z.string(),
          totalCents: z.number().int().nonnegative(),
          trips: z.number().int().nonnegative(),
        }),
      ),
      /** Spend per day across the window, zero-filled, oldest first. */
      daily: z.array(
        z.object({
          date: z.string(), // YYYY-MM-DD
          totalCents: z.number().int().nonnegative(),
        }),
      ),
    })
    .nullable(),
});
export type ReportsOperationsDashboardResponse = z.infer<
  typeof reportsOperationsDashboardResponseSchema
>;

// ----- Reports → manual digest trigger ----------------------------------

/** Request payload for `POST /api/admin/reports/send-weekly-digest-now`.
 *  All fields optional: by default we send both digests to the
 *  authenticated caller's email only (so testing doesn't spam every
 *  super-admin). Set `mode=all_super_admins` to fan out for real. */
// =============================================================================
// Report recipients — who receives the scheduled report emails.
// Managed from the Reports page's "Recipients" dialog (super-admin
// only). The pickable pool is active admins + super-admins.
// =============================================================================

export const reportRecipientSchema = z.object({
  userId: z.string().uuid(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  role: userRoleSchema,
  department: z.string(),
  /** Receives the weekly operations digest. LEGACY: derived from
   *  `subscriptions` and kept only so an older client keeps rendering. The
   *  dialog reads `subscriptions`. */
  weekly: z.boolean(),
  /** Receives the monthly all-departments report (with CSV). Legacy, as above. */
  monthly: z.boolean(),
  /** Every (report, cadence) this person receives. The authoritative shape: a
   *  person may take one report at several cadences, which the two booleans
   *  above cannot express. Defaulted so an older API response still parses. */
  subscriptions: z
    .array(
      z.object({
        reportType: z.string(),
        cadence: z.enum(["daily", "weekly", "monthly"]),
      }),
    )
    .default([]),
});
export type ReportRecipient = z.infer<typeof reportRecipientSchema>;

export const reportRecipientsResponseSchema = z.object({
  recipients: z.array(reportRecipientSchema),
});
export type ReportRecipientsResponse = z.infer<
  typeof reportRecipientsResponseSchema
>;

/** The COMPLETE desired subscription set — every (person, report, cadence)
 *  that should exist after the save. Sending the whole set rather than deltas
 *  means the server never has to reconcile a partial view: anything absent is
 *  unsubscribed, full stop. The cap is generous but bounded (people x reports
 *  x cadences) so a malformed client cannot ask for an unbounded write. */
export const reportRecipientsUpdateRequestSchema = z.object({
  /** The people the client actually rendered, and the reports it knew about.
   *  Together they bound what a complete-state save is allowed to unsubscribe.
   *
   *  Without them the server has to guess the scope, and both guesses are
   *  wrong: its own current pool speaks for people who entered it after the
   *  dialog loaded, and its own registry speaks for reports an older cached
   *  bundle never showed. A cell the admin could not see is a cell they did
   *  not decide about. */
  knownUserIds: z.array(z.string().uuid()).min(1).max(500),
  /** Every (report, cadence) CHECKBOX COLUMN the client rendered. Cells, not
   *  report names: the cadence axis is negotiable too. A bundle that knows a
   *  report but not a cadence added to it since would, under a report-level
   *  scope, delete the column it never drew. */
  knownReportCadences: z
    .array(
      z.object({
        reportType: z.string().min(1).max(64),
        cadence: z.enum(["daily", "weekly", "monthly"]),
      }),
    )
    .min(1)
    .max(192),
  subscriptions: z
    .array(
      z.object({
        userId: z.string().uuid(),
        reportType: z.string().min(1).max(64),
        cadence: z.enum(["daily", "weekly", "monthly"]),
      }),
    )
    .max(2000),
});
export type ReportRecipientsUpdateRequest = z.infer<
  typeof reportRecipientsUpdateRequestSchema
>;

/** POST /reports/send-offsite-review-now.
 *
 *  Exists because the other three reports have a "send it to me" preview and
 *  this one did not — so the only way to see the off-site email was to
 *  subscribe someone and wait for the scheduler. `self` is the default for the
 *  same reason it is elsewhere: a preview must not be one misclick away from
 *  mailing the whole recipient list. */
export const reportsSendOffsiteReviewRequestSchema = z.object({
  mode: z.enum(["self", "recipients"]).default("self"),
  /** Which window to render. The report reads the same way at any cadence;
   *  this only chooses the period the preview covers. */
  cadence: z.enum(["daily", "weekly", "monthly"]).default("monthly"),
});
export type ReportsSendOffsiteReviewRequest = z.infer<
  typeof reportsSendOffsiteReviewRequestSchema
>;

export const reportsSendWeeklyDigestRequestSchema = z.object({
  /** "self" (default) — send only to the caller's email so testing
   *  doesn't fire the digest at every configured recipient every time
   *  someone clicks the button.
   *
   *  "recipients" — fan out to the configured weekly recipients (the
   *  Recipients dialog), falling back to all active super-admins when
   *  none are configured. "all_super_admins" is the legacy alias for
   *  the same fallback path, kept for compat. */
  mode: z.enum(["self", "recipients", "all_super_admins"]).default("self"),
  /** When true, also send the patient-cleanup digest (gated on >0
   *  flagged members). Defaults true so the test surface matches the
   *  cron job. */
  includeCleanup: z.boolean().default(true),
  /** Window size for the digest. `7` (default) gives the standard
   *  weekly digest — last 7 days vs prior 7 days. `1` produces a
   *  "daily" variant — last 24h vs prior 24h — with a different
   *  subject line + window label in the email. Reuses the same
   *  template, so we don't fork the rendering logic. Ignored when an
   *  explicit dateFrom/dateTo range is provided. */
  windowDays: z.union([z.literal(1), z.literal(7)]).default(7),
  /** Optional explicit report range (YYYY-MM-DD, inclusive). When set,
   *  the digest covers [dateFrom, dateTo] and compares against the
   *  equal-length window immediately before it. */
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
});
export type ReportsSendWeeklyDigestRequest = z.infer<
  typeof reportsSendWeeklyDigestRequestSchema
>;

export const reportsSendWeeklyDigestResponseSchema = z.object({
  weeklyDigest: z.object({
    recipients: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
  cleanupDigest: z.object({
    /** When null, no cleanup digest was sent because the cleanup count
     *  was 0. */
    sent: z.number().int().nonnegative().nullable(),
    failed: z.number().int().nonnegative().nullable(),
    flaggedTotal: z.number().int().nonnegative().nullable(),
  }),
});
export type ReportsSendWeeklyDigestResponse = z.infer<
  typeof reportsSendWeeklyDigestResponseSchema
>;

// ----- Reports → monthly department summary preview ---------------------

/** Request shape for `POST /api/admin/reports/send-department-summary-now`.
 *  Behaves like the weekly-digest preview: defaults to "self" to avoid
 *  spamming everyone whenever someone clicks the button. */
export const reportsSendDepartmentSummaryRequestSchema = z.object({
  /** When omitted, the job iterates EVERY department that had bookings
   *  last month and fans out — same as the monthly cron. Pass a single
   *  department name to scope the preview to just that one. */
  department: z.string().min(1).max(100).optional(),
  /** "self" (default) sends only to the caller's email so previews
   *  cost nothing. "all_recipients" fans out per the same recipient
   *  rules the cron uses (admins matching the department + every
   *  super_admin). */
  mode: z.enum(["self", "all_recipients"]).default("self"),
});
export type ReportsSendDepartmentSummaryRequest = z.infer<
  typeof reportsSendDepartmentSummaryRequestSchema
>;

export const reportsSendDepartmentSummaryResponseSchema = z.object({
  /** "April 2026" — what month the summary covered. */
  monthLabel: z.string(),
  /** One entry per department that was processed. */
  departments: z.array(
    z.object({
      department: z.string(),
      recipients: z.number().int().nonnegative(),
      sent: z.number().int().nonnegative(),
      failed: z.number().int().nonnegative(),
    }),
  ),
});
export type ReportsSendDepartmentSummaryResponse = z.infer<
  typeof reportsSendDepartmentSummaryResponseSchema
>;

// =============================================================================
// Monthly all-departments report — ONE email containing every
// department's numbers for the month plus a CSV attachment of all the
// month's bookings (filterable in Excel). Replaces the old one-email-
// per-department fan-out that buried recipients in 20+ messages.
// =============================================================================

export const reportsSendMonthlyReportRequestSchema = z.object({
  /** "self" (default) previews to the caller only; "recipients" sends
   *  to the configured monthly recipients (fallback: super-admins). */
  mode: z.enum(["self", "recipients"]).default("self"),
  /** Month to report on, "YYYY-MM". Defaults to the prior calendar
   *  month (same as the scheduled job). */
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/u)
    .optional(),
});
export type ReportsSendMonthlyReportRequest = z.infer<
  typeof reportsSendMonthlyReportRequestSchema
>;

export const reportsSendOffsiteReviewResponseSchema = z.object({
  sent: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  /** Off-site trips in the window — the number the email leads with. */
  offSiteTrips: z.number().int().nonnegative(),
  windowLabel: z.string(),
});
export type ReportsSendOffsiteReviewResponse = z.infer<
  typeof reportsSendOffsiteReviewResponseSchema
>;

export const reportsSendMonthlyReportResponseSchema = z.object({
  monthLabel: z.string(),
  recipients: z.number().int().nonnegative(),
  sent: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  /** Rows in the attached CSV (bookings in the month). */
  csvRows: z.number().int().nonnegative(),
});
export type ReportsSendMonthlyReportResponse = z.infer<
  typeof reportsSendMonthlyReportResponseSchema
>;
